import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Box, Button } from "@mui/material/";

import { strings } from "../../common/i18n";
import { helpers } from "../../common/lib";
import actions from "../../Store/actions";
import {
    useAssetTypesForOrganisation,
    useMakesForAssetTypes
} from "../../common/hooks";
import EditIcon from "@mui/icons-material/Edit";
import styles from "./styles";
import { Typography } from "@mui/material/";
import CircleIcon from "../../common/components/CircleIcon";
import { csv } from "../../common/lib";
import { Download } from "@mui/icons-material";
import { logToSentry } from "../../config/Sentry";

/**
 *  Hook for user table data and operations
 */

export default () => {
    const dispatch = useDispatch();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { assets, typesList, branches } = useSelector((state) => state);

    useEffect(() => {
        //Load assets

        if (!loading) {
            setLoading(true);
            (async () => {
                try {
                    await dispatch(actions.assets.create.getRequest());
                    setLoading(false);
                } catch (error) {
                    const errStr = error.toString();
                    console.error(errStr);
                    logToSentry("On loading assets:", { error });
                    dispatch(
                        actions.toast.create.error(
                            strings("errors.onLoadAssets")
                        )
                    );
                }
            })();
        }
    }, [assets.data]);

    const getIdentifiers = ({
        identifier1,
        identifier2,
        identifier3,
        identifier4
    }) => ({
        identifier1,
        identifier2,
        identifier3,
        identifier4
    });

    /** On editing columns */

    const onEdit = (row) => {
        try {
            setDialogOpen(true);
            const { id } = row;
            dispatch(actions.assets.create.itemSet(assets.data[id]));
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On editing assets:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onLoadAssets"))
            );
        }
    };

    /** On close  */
    const onDialogClose = async () => {
        try {
            clearItem();
            await dispatch(actions.assets.create.getRequest());
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On updating assets:", { error });
            dispatch(actions.toast.create.error(strings("errors.onEditAsset")));
        }
    };

    /** On download all assets  */
    const onDownloadAssets = () => {
        csv.download(columns, data, `asset-list-summary`);
    };

    /** Clear when nothing selected  */
    const clearItem = () => {
        dispatch(actions.assets.create.itemClear());
    };

    /** Rows data */
    const data = useMemo(() => {
        if (!loading && assets.data.length > 0) {
            const makesData = typesList.makes.data;
            const makesById = typesList.makes.byId;
            const typesData = typesList.assetTypes.data;
            const typesById = typesList.assetTypes.byId;
            const branchesData = branches.data;
            const branchesById = branches.byId;
            return assets.data.map((item, index) => {
                const { asset, isLive, isExpected } = item;
                if (asset)
                    return {
                        ...getIdentifiers(asset),
                        id: index,
                        make: makesData[makesById[asset.make]]?.name ?? "",
                        type: typesData[typesById[asset.type]]?.name ?? "",
                        branchName: branchesData[branchesById[item.branch]]?.name ?? "",
                        isLive: strings(isLive ? "yes" : "no"),
                        isExpected: strings(isExpected ? "yes" : "no")
                    };
                //If no asset returned just return id
                else return { id: index };
            });
        } else return [];
    }, [assets, typesList, branches, loading]);

    /** Asset Types and makes*/
    const assetTypes = useAssetTypesForOrganisation();
    const makes = useMakesForAssetTypes(assetTypes.map((item) => item.id));
    const isExpectedFilter = useMemo(() => {
        const arr = [];
        data.map((item) => {
            if (!arr.includes(item.isExpected)) arr.push(item.isExpected);
        });
        return arr;
    }, [data]);

    const columns = useMemo(
        () => [
            {
                id: "identifier1",
                header: strings("identifier1"),
                accessorKey: "identifier1",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            },
            {
                id: "identifier2",
                header: strings("identifier2"),
                accessorKey: "identifier2",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            },

            {
                id: "make",
                header: strings("make"),
                accessorKey: "make",
                filterVariant: "autocomplete",
                size: 100
            },
            {
                id: "type",
                header: strings("type"),
                accessorKey: "type",
                filterVariant: "autocomplete",
                size: 100
            },
            {
                id: "branchName",
                header: strings("branchName"),
                filterVariant: "autocomplete",
                accessorKey: "branchName",
                size: 150
            },
            {
                id: "isExpected",
                header: strings("isExpected"),
                accessorKey: "isExpected",
                size: 50,
                Cell: ({ cell }) => {
                    let value = cell?.getValue();

                    if (cell?.getValue()?.toLowerCase() === "yes") value = true;
                    else if (cell?.getValue()?.toLowerCase() === "no")
                        value = false;
                    else value = "none";
                    return (
                        <>
                            <Typography style={{ fontSize: 0 }}>
                                {cell.isExpected}
                            </Typography>
                            <CircleIcon value={value} />
                        </>
                    );
                }
            }
        ],
        []
    );

    const rowActions = (row) => {
        const { branchName } = row.original;
        return [
            branchName && (
                <IconButton key="edit" onClick={() => onEdit(row.original)}>
                    <EditIcon color="primary" />
                </IconButton>
            )
        ];
    };

    /** Toolbar Component */

    const toolBarComponents = () => (
        <IconButton
            sx={styles.primaryButton}
            variant="contained"
            size="small"
            color="primary.main"
            onClick={onDownloadAssets}
            title={strings("downloadAllAssets")}
        >
            <Download />
        </IconButton>
    );

    return {
        columns,
        dialogOpen,
        onDialogClose,
        onDownloadAssets,
        clearItem,
        data,
        loading,
        assetItem: assets.item,
        toolBarComponents,
        rowActions
    };
};
